import { UploadFile } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { Autocomplete, IconButton, List, ListItem, ListItemButton } from "@mui/material";
import Grid from '@mui/material/Grid';
import React, { useEffect } from "react";
import { useDropzone } from 'react-dropzone';
import { toast } from "react-toastify";
import { PortalDocumentService } from '../../../Component/Api/PortalDocument/PortalDocumentUploadService';
import { FormField } from '../../../Component/Common/Components/FormField';
import { TinyLoading } from '../../../Component/Common/Components/Loading/IntermediateLoading';
import { MDAlert } from '../../../Component/Common/Components/MDAlert';
import { MDBox } from '../../../Component/Common/Components/MDBox';
import { MDTypography } from '../../../Component/Common/Components/MDTypography';
import { Tooltip } from '../../../Component/Common/Components/Tooltip/Tooltip';
import { PortalDocumentDto, PortalDocumentRequest } from '../../../Component/Model/Dto/PortalDocument';

interface IProps {
    link: string;
    id: number;
}

export const PortalDocumentsDropZone = (props: IProps) => {

    const [documentTypes, setDocumentTypes] = React.useState<PortalDocumentDto[]>([]);
    const [documentsUploaded, setDocumentsUploaded] = React.useState<PortalDocumentDto[]>([]);
    const [documentsNotUploaded, setDocumentsNotUploaded] = React.useState<PortalDocumentDto[]>([]);
    const [documentTypeId, setDocumentTypeId] = React.useState<number>(-1);
    const [isUploading, setIsUploading] = React.useState(false);

    const documentSearchRequest: PortalDocumentRequest = {
        id: props.id,
        link: props.link,
    };

    const dzStyles = {
        display: 'flex',
        border: 'dashed 3px #eee',
        borderColor: "black",
        borderRadius: '5px',
        alignItems: 'center',
        justifyContent: 'center',
        height: 200,
        width: '100%'
    }

    const dzActive = {
        borderColor: 'green'
    }

    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: {
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
            'image/bmp': ['.bmp'],
            'image/tiff': ['.tiff'],
            'application/msword': ['.doc'],
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
            'application/vnd.ms-excel': ['.xls'],
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
            'application/pdf': ['.pdf'],
            'application/zip': ['.zip'],
            'application/x-rar-compressed': ['.rar'],
        },
        maxSize: 1024 * 1024 * 20,  // limit to 20MB
        onDrop: (acceptedFiles, fileRejections) => {
            if (fileRejections.length > 0) {
                const error = fileRejections[0].errors[0];
                let message = error.message;

                if (error.code === 'file-too-large') {
                    message = 'File is bigger than 20MB';
                } else if (error.code === 'file-invalid-type') {
                    message = 'Unsupported file type. Permitted file types are: .jpeg, .jpg, .png, .gif, .bmp, .tiff, .doc, .docx, .xls, .xlsx, .pdf, .zip, .rar';
                }
                toast.error(`${message}`);
            } else {
                const document = acceptedFiles[0];
                try {
                    uploadFile(document);
                } catch (error) {
                    toast.error("An error occurred while uploading the file");
                }
            }
        }
    });

    const uploadFile = async (document: any) => {
        setIsUploading(true);
        const headers = { 'Content-Type': 'multipart/form-data' };
        let formData = new FormData();

        formData.append('Id', props.id.toString());
        formData.append('Link', props.link);
        formData.append('File', document, document.name);
        formData.append('DocumentTypeId', documentTypeId.toString());
        try {
            await PortalDocumentService.UploadDocument(formData, headers);
            toast.success("File successfully uploaded");
            getUploadedDocuments();
        } catch (error) {
            toast.error('File not uploaded');
            console.log(error)
        } finally {
            setIsUploading(false);
        }
    }

    const getDocument = async (id: number) => {
        try {
            const response = await PortalDocumentService.GetById(id);
            window.open(response.data);
        } catch (error) { }
    }

    const deleteDocument = async (id: number) => {
        try {
            await PortalDocumentService.DeleteDocument(id, props.link);
            toast.success("File successfully deleted ");
            getUploadedDocuments();

        } catch (error) {
            console.log(error)
        }
    }

    const getDocumentTypes = async () => {
        try {
            const results = await PortalDocumentService.GetDocumentTypesForSection(props.link);
            setDocumentTypes(results.data);        
            if (results.data.length === 1) {
                const defaultDocumentId = results.data[0].documentTypeId;
                setDocumentTypeId(defaultDocumentId);
            }
        } catch (error) {
            console.log(error)
        }
    }

    const getUploadedDocuments = async () => {
        try {
            const results = await PortalDocumentService.GetDocumentsUploaded(documentSearchRequest);
            setDocumentsNotUploaded(results.data.documentsNotUploaded);
            setDocumentsUploaded(results.data.documentsUploaded);
        } catch (error) {
            console.log(error)
        }
    }

    const fetchData = async () => {
        const documentTypePromise = getDocumentTypes();
        const uploadedDocumentsPromise = getUploadedDocuments();
        await Promise.all([documentTypePromise, uploadedDocumentsPromise]);
    }

    useEffect(() => {
        fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <>
            <MDTypography variant='h6' fontWeight='bold' mb={1}>
                Supporting Documents
            </MDTypography>
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    {documentTypes.length > 1 &&
                        <MDBox mb={2}>
                            <Autocomplete
                                style={{ paddingTop: "5px" }}
                                options={documentTypes}
                                getOptionLabel={option => option.documentName}
                                onChange={(e: any, newValue: any) => {
                                    if (newValue) {
                                        setDocumentTypeId(newValue.documentTypeId);
                                    } else {
                                        setDocumentTypeId(-1);
                                    }
                                }}
                                renderOption={(props, option) => {
                                    return (
                                        <li {...props} key={option.documentTypeId}>
                                            {option.documentName}
                                        </li>
                                    );
                                }}
                                renderInput={(params) => <FormField variant="outlined" label='Please select the type of document to be uploaded' {...params} type='text' required />}
                            />
                        </MDBox>
                    }
                    {documentTypeId > 0 &&
                        <>
                            <MDBox display="flex" color="white">
                                <MDTypography component='span' variant='body' fontSize='0.93rem' fontWeight='light'>
                                    <p> The maximum file size for any upload is <strong>20MB</strong> per file.</p>
                                    <MDTypography component='span' variant='body' fontWeight='bold' fontSize='0.95rem' mb={1} display="flex" alignItems="center">
                                        Only the following file types are allowed:
                                    </MDTypography>
                                    <p> *.jpeg / *.jpg / *.png / *.gif / *.bmp / *.tiff / *.doc / *.docx / *.xls / *.xlsx / *.pdf / *.zip / *.rar / </p>
                                </MDTypography>
                            </MDBox>
                            {isUploading ?
                                <MDBox style={dzStyles}>
                                    <TinyLoading />
                                </MDBox>
                                :
                                <MDBox {...getRootProps()} style={isDragActive ? { ...dzStyles, ...dzActive } : dzStyles}>
                                    <input {...getInputProps()} />
                                    <div style={isDragActive ? { color: 'green', display: 'flex', flexDirection: 'column', alignItems: 'center' } : { display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                        <UploadFile style={{ width: '10vh', height: '10vh' }} />
                                        <MDTypography variant="h4" style={{ textAlign: 'center' }} sx={{ color: 'inherit' }}>
                                            Drag file or click here
                                        </MDTypography>
                                    </div>
                                </MDBox>}
                        </>
                    }
                </Grid>
                <Grid item xs={6}>
                    {documentTypes.length > 1 &&
                        <>
                            <MDAlert color='light'>
                                <MDTypography component='span' variant='body' fontSize='0.83rem' fontWeight='regular' >
                                    <MDTypography component='span' variant='body' fontSize='0.87rem' fontWeight='medium' mb={1} display="flex" alignItems="center" >
                                        The following supporting documents should be uploaded:
                                    </MDTypography>
                                    
                                    {documentsNotUploaded.length === 0 && <p>All mandatory documents have been successfully uploaded, there are no missing items.</p>}
                                    
                                    <ul>{
                                        documentsNotUploaded.map((type, index) =>
                                            <li key={index} style={{ margin: '3px', marginBottom: '10px' }}>
                                                {type.documentType}
                                            </li>
                                        )
                                    }</ul>
                                </MDTypography>
                            </MDAlert>
                        </>
                    }
                    {documentsUploaded.length > 0 &&
                        <MDAlert color='info'>
                            <MDTypography component='span' variant='body' fontSize='0.83rem' fontWeight='regular' color style={{ width: '100%' }}>
                                <MDTypography component='span' variant='body' fontSize='0.87rem' fontWeight='medium' mb={1} display="flex" alignItems="left" color>
                                    The following files have been uploaded:
                                </MDTypography>
                                <List sx={{ listStyleType: 'disc', pl: 1 }}>{
                                    documentsUploaded.map((doc, index) => {
                                        return (
                                            <ListItem key={index}
                                               secondaryAction={
                                                <IconButton edge="end" aria-label="delete" color='inherit' 
                                                    onClick={(event) => {
                                                        deleteDocument(doc.documentId)
                                                    }}>
                                                  <CloseIcon fontSize="small" />
                                                </IconButton>}
                                                sx={{ display: 'list-item' }}>

                                                {doc.showTooltip ?
                                                    <Tooltip title={doc.fullNameAndType}>
                                                        <ListItemButton
                                                            onClick={(event) => {
                                                                event.preventDefault();
                                                                getDocument(doc.documentId)
                                                            }}>
                                                            {doc.shortenedName}
                                                        </ListItemButton>
                                                    </Tooltip> :
                                                    <ListItemButton
                                                        onClick={(event) => {
                                                            event.preventDefault();
                                                            getDocument(doc.documentId)
                                                        }}>
                                                        {doc.fullNameAndType}
                                                    </ListItemButton>
                                                    
                                                }
                                            </ListItem>
                                        )
                                    })
                                }</List>
                            </MDTypography>
                        </MDAlert>
                    }
                </Grid>
            </Grid >
        </>
    )
}